import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { PlaybackService } from '../../../core/services/playback.service';
import { SpotifyService } from '../../../core/services/spotify.service';
import { WebPlaybackTrack, PlaybackState } from '../../../shared/models/spotify.models';

@Component({
  selector: 'app-queue',
  templateUrl: './queue.component.html',
  styleUrls: ['./queue.component.scss']
})
export class QueueComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();

  // State properties
  currentTrack: WebPlaybackTrack | null = null;
  nextTracks: WebPlaybackTrack[] = [];
  previousTracks: WebPlaybackTrack[] = [];
  playbackState: PlaybackState | null = null;
  isPlayerReady: boolean = false;
  isPremium: boolean = false;

  // UI state
  showPreviousTracks: boolean = false;
  isReordering: boolean = false;

  constructor(
    private playbackService: PlaybackService,
    private spotifyService: SpotifyService
  ) {}

  ngOnInit(): void {
    this.subscribeToPlaybackState();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private subscribeToPlaybackState(): void {
    // Subscribe to player ready state
    this.playbackService.isReady$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isReady => {
        this.isPlayerReady = isReady;
      });

    // Subscribe to premium status
    this.playbackService.isPremium$
      .pipe(takeUntil(this.destroy$))
      .subscribe(isPremium => {
        this.isPremium = isPremium;
      });

    // Subscribe to playback state
    this.playbackService.playbackState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.playbackState = state;
        if (state?.track_window) {
          this.currentTrack = state.track_window.current_track;
          this.nextTracks = [...state.track_window.next_tracks];
          this.previousTracks = [...state.track_window.previous_tracks];
        }
      });
  }

  // Queue manipulation methods
  async playTrackAtIndex(index: number): Promise<void> {
    if (!this.isPlayerReady || index < 0 || index >= this.nextTracks.length) {
      return;
    }

    try {
      // Skip to the track by calling next multiple times
      for (let i = 0; i <= index; i++) {
        await this.playbackService.nextTrack();
        // Small delay to prevent rate limiting
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.error('Failed to play track at index:', error);
    }
  }

  async removeFromQueue(index: number): Promise<void> {
    if (!this.isPlayerReady || index < 0 || index >= this.nextTracks.length) {
      return;
    }

    try {
      // Note: Spotify Web Playback SDK doesn't support removing tracks from queue
      // This would require using the Spotify Web API Add/Remove from Queue endpoints
      // For now, we'll show a message
      console.log('Remove from queue not implemented - requires Spotify Web API queue management');
    } catch (error) {
      console.error('Failed to remove from queue:', error);
    }
  }

  async addToQueue(trackUri: string): Promise<void> {
    if (!this.isPlayerReady) {
      return;
    }

    try {
      // This would need to be implemented in the backend Spotify service
      console.log('Add to queue:', trackUri);
    } catch (error) {
      console.error('Failed to add to queue:', error);
    }
  }

  async clearQueue(): Promise<void> {
    if (!this.isPlayerReady) {
      return;
    }

    try {
      // Note: Spotify doesn't provide a direct "clear queue" API
      // This would require complex logic to skip through all tracks
      console.log('Clear queue not directly supported by Spotify API');
    } catch (error) {
      console.error('Failed to clear queue:', error);
    }
  }

  // Drag and drop (note: limited by Spotify API)
  onQueueDrop(event: CdkDragDrop<WebPlaybackTrack[]>): void {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    // Update local state optimistically
    moveItemInArray(this.nextTracks, event.previousIndex, event.currentIndex);
    
    // Note: Spotify Web Playback SDK doesn't support queue reordering
    // This would require implementing with Spotify Web API queue management
    console.log('Queue reordering not supported by Spotify Web Playback SDK');
  }

  // Utility methods
  getTrackArtists(track: WebPlaybackTrack): string {
    return track?.artists?.map(artist => artist.name).join(', ') || '';
  }

  formatDuration(durationMs: number): string {
    return this.playbackService.formatDuration(durationMs);
  }

  getAlbumImage(track: WebPlaybackTrack): string | null {
    if (!track?.album?.images || track.album.images.length === 0) {
      return null;
    }
    
    // Return the smallest image for queue items
    const sortedImages = track.album.images.sort((a, b) => (a.width || 0) - (b.width || 0));
    return sortedImages[0]?.url || null;
  }

  // Toggle methods
  togglePreviousTracks(): void {
    this.showPreviousTracks = !this.showPreviousTracks;
  }

  // Queue statistics
  getTotalQueueDuration(): number {
    return this.nextTracks.reduce((total, track) => total + track.duration_ms, 0);
  }

  getQueueTrackCount(): number {
    return this.nextTracks.length;
  }

  formatQueueDuration(): string {
    const totalMs = this.getTotalQueueDuration();
    const totalMinutes = Math.floor(totalMs / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  }

  // Context actions
  async saveQueueAsPlaylist(): Promise<void> {
    if (!this.nextTracks.length) {
      return;
    }

    try {
      const trackUris = this.nextTracks.map(track => track.uri);
      const playlistName = `Queue - ${new Date().toLocaleDateString()}`;
      
      // Create new playlist with queue tracks
      const playlist = await this.spotifyService.createPlaylist({
        name: playlistName,
        description: `Queue saved on ${new Date().toLocaleDateString()}`,
        public: false
      }).toPromise();

      // Add tracks to the playlist
      if (playlist) {
        await this.spotifyService.addTracksToPlaylist(playlist.id, trackUris).toPromise();
        console.log('Queue saved as playlist:', playlist.name);
      }
    } catch (error) {
      console.error('Failed to save queue as playlist:', error);
    }
  }

  // Navigation methods
  goToPreviousTrack(): void {
    this.playbackService.previousTrack();
  }

  goToNextTrack(): void {
    this.playbackService.nextTrack();
  }

  // Share queue
  shareQueue(): void {
    if (!this.nextTracks.length) {
      return;
    }

    const queueText = this.nextTracks
      .slice(0, 5) // Limit to first 5 tracks
      .map(track => `${track.name} - ${this.getTrackArtists(track)}`)
      .join('\n');

    const shareText = `My Spotify Queue:\n${queueText}${this.nextTracks.length > 5 ? `\n...and ${this.nextTracks.length - 5} more tracks` : ''}`;

    if (navigator.share) {
      navigator.share({
        title: 'My Spotify Queue',
        text: shareText
      }).catch(console.error);
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(shareText).then(() => {
        console.log('Queue shared to clipboard');
      }).catch(console.error);
    }
  }

  // TrackBy function for ngFor performance
  trackByUri(index: number, track: WebPlaybackTrack): string {
    return track.uri;
  }
}